import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaidIcon from '@mui/icons-material/Paid';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { timeClockAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { formatDate, formatUtcDateTime, parseServerUtc } from '../utils/dateFormat';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const formatHours = (hours) => Number(hours || 0).toFixed(2);

const formatMoney = (amount) =>
  amount == null ? '-' : `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pad = (n) => String(n).padStart(2, '0');

// Today in the viewer's time zone, as YYYY-MM-DD.
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Server naive-UTC timestamp → the local "YYYY-MM-DDTHH:mm" a datetime-local input wants.
const toLocalInput = (value) => {
  const d = parseServerUtc(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// datetime-local value (viewer's local time) → ISO instant with offset for the API.
const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

// One worker row, plus their unpaid shifts in a collapsible sub-table.
function WorkerRow({ worker, onMarkPaid, onEditEntry, onDeleteEntry }) {
  const [open, setOpen] = useState(false);
  const entries = [
    ...(worker.open_entry ? [worker.open_entry] : []),
    ...[...worker.unpaid_entries].reverse(),
  ];

  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell align="center" sx={actionsKabobCellSx}>
          <RowActionsMenu
            actions={[
              worker.payable_hours >= 0.5 && {
                key: 'paid', label: 'Mark Paid', icon: <PaidIcon fontSize="small" />, onClick: () => onMarkPaid(worker),
              },
            ]}
          />
        </TableCell>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={open ? 'Hide shifts' : 'Show shifts'}
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{worker.name}</TableCell>
        <TableCell>
          {worker.open_entry
            ? <Chip size="small" color="success" label="Clocked in" />
            : <Chip size="small" variant="outlined" label="Clocked out" />}
        </TableCell>
        <TableCell align="right">{formatHours(worker.unpaid_hours)}</TableCell>
        <TableCell align="right">{formatHours(worker.payable_hours)}</TableCell>
        <TableCell>{worker.last_payment ? formatDate(worker.last_payment.paid_date) : 'Never'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, ml: { sm: 6 } }}>
              <Typography variant="subtitle2" gutterBottom>
                Unpaid shifts
                {worker.carryover_hours > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}· plus {formatHours(worker.carryover_hours)} hrs carried over from last pay
                  </Typography>
                )}
              </Typography>
              <TableContainer sx={{ maxHeight: 360, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                      <TableCell>Clock In</TableCell>
                      <TableCell>Clock Out</TableCell>
                      <TableCell align="right">Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell align="center" sx={actionsKabobCellSx}>
                          <RowActionsMenu
                            actions={[
                              { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEditEntry(entry) },
                              { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => onDeleteEntry(entry), color: 'error' },
                            ]}
                          />
                        </TableCell>
                        <TableCell>{formatUtcDateTime(entry.clock_in)}</TableCell>
                        <TableCell>{entry.clock_out ? formatUtcDateTime(entry.clock_out) : 'Still clocked in'}</TableCell>
                        <TableCell align="right">{entry.hours == null ? '-' : formatHours(entry.hours)}</TableCell>
                      </TableRow>
                    ))}
                    {entries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No unpaid shifts.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const AdminTimeClockPage = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [payments, setPayments] = useState([]);

  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ paid_date: '', amount: '', notes: '' });
  const payFields = useFieldErrors();

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: '', clock_out: '' });
  const editFields = useFieldErrors();

  const [deleteEntryTarget, setDeleteEntryTarget] = useState(null);
  const [deletePaymentTarget, setDeletePaymentTarget] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [workersRes, paymentsRes] = await Promise.all([
        timeClockAPI.getWorkers(),
        timeClockAPI.getPayments(),
      ]);
      setWorkers(workersRes.data);
      setPayments(paymentsRes.data);
    } catch (err) {
      notify.error(errorMessage(err, 'Failed to load time clock'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- mark paid ---

  const handleOpenPay = (worker) => {
    payFields.reset();
    setPayForm({ paid_date: todayLocal(), amount: '', notes: '' });
    setPayTarget(worker);
  };

  const handleSavePay = async () => {
    if (!payFields.validate({
      paid_date: !payForm.paid_date && 'Paid date is required',
      amount: payForm.amount !== '' && Number(payForm.amount) < 0 && 'Amount cannot be negative',
    })) return;

    setSaving(true);
    try {
      await timeClockAPI.markPaid(payTarget.user_id, payForm);
      setPayTarget(null);
      await loadData();
    } catch (err) {
      notify.error(errorMessage(err, 'Failed to record payment'));
    } finally {
      setSaving(false);
    }
  };

  // --- edit / delete a shift ---

  const handleOpenEdit = (entry) => {
    editFields.reset();
    setEditForm({ clock_in: toLocalInput(entry.clock_in), clock_out: toLocalInput(entry.clock_out) });
    setEditTarget(entry);
  };

  const handleSaveEdit = async () => {
    if (!editFields.validate({
      clock_in: !editForm.clock_in && 'Clock-in time is required',
      clock_out: (!editForm.clock_out && editTarget.clock_out && 'Clock-out time is required')
        || (editForm.clock_out && editForm.clock_out <= editForm.clock_in && 'Clock-out must be after clock-in'),
    })) return;

    setSaving(true);
    try {
      await timeClockAPI.updateEntry(editTarget.id, {
        clock_in: fromLocalInput(editForm.clock_in),
        clock_out: fromLocalInput(editForm.clock_out),
      });
      setEditTarget(null);
      await loadData();
    } catch (err) {
      notify.error(errorMessage(err, 'Failed to update shift'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    const entry = deleteEntryTarget;
    setDeleteEntryTarget(null);
    try {
      await timeClockAPI.deleteEntry(entry.id);
      await loadData();
    } catch (err) {
      notify.error(errorMessage(err, 'Failed to delete shift'));
    }
  };

  const handleDeletePayment = async () => {
    const payment = deletePaymentTarget;
    setDeletePaymentTarget(null);
    try {
      await timeClockAPI.deletePayment(payment.id);
      await loadData();
    } catch (err) {
      notify.error(errorMessage(err, 'Failed to delete payment'));
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>Time Clock</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Hours each worker is owed since they were last paid. Pay is in half-hour increments:
        Mark Paid pays the owed hours rounded down to the half hour and carries the rest into
        the next payment. A shift still in progress rolls into the next payment. Expand a
        worker to fix a shift, such as a forgotten clock-out.
      </Typography>

      <Typography variant="h6" sx={{ mb: 1 }}>Workers</Typography>
      <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell padding="checkbox" />
              <TableCell>Worker</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Hours Owed</TableCell>
              <TableCell align="right">Payable</TableCell>
              <TableCell>Last Paid</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={7} rows={3} />}
            {!loading && workers.map((worker) => (
              <WorkerRow
                key={worker.user_id}
                worker={worker}
                onMarkPaid={handleOpenPay}
                onEditEntry={handleOpenEdit}
                onDeleteEntry={setDeleteEntryTarget}
              />
            ))}
            {!loading && workers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No workers yet. Give a user the Inventory Counter role to add them.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>Pay Log</Typography>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>Paid Date</TableCell>
              <TableCell>Worker</TableCell>
              <TableCell align="right">Hours Paid</TableCell>
              <TableCell align="right">Carried Over</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={7} rows={3} />}
            {!loading && payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  {/* Only a worker's latest payment can be undone: an older one's
                      carryover has already been paid out in the next. */}
                  <RowActionsMenu
                    actions={[
                      payment.is_latest && { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => setDeletePaymentTarget(payment), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{formatDate(payment.paid_date)}</TableCell>
                <TableCell>{payment.name}</TableCell>
                <TableCell align="right">{formatHours(payment.hours)}</TableCell>
                <TableCell align="right">{formatHours(payment.carryover_hours)}</TableCell>
                <TableCell align="right">{formatMoney(payment.amount)}</TableCell>
                <TableCell>{payment.notes || '-'}</TableCell>
              </TableRow>
            ))}
            {!loading && payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No payments recorded yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mark paid */}
      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark {payTarget?.name} Paid</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              Pays <strong>{formatHours(payTarget?.payable_hours)} hours</strong>
              {' '}({formatHours(payTarget?.worked_hours)} worked across{' '}
              {payTarget?.unpaid_entries.length} shift{payTarget?.unpaid_entries.length === 1 ? '' : 's'}
              {payTarget?.carryover_hours > 0 && ` + ${formatHours(payTarget.carryover_hours)} carried over`}).
            </Typography>
            {payTarget?.carryover_after_pay > 0 && (
              <Typography variant="body2" color="text.secondary">
                {formatHours(payTarget.carryover_after_pay)} hours carry to the next pay.
              </Typography>
            )}
            <TextField
              size="small"
              label="Paid Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={payForm.paid_date}
              onChange={(e) => { payFields.clear('paid_date'); setPayForm({ ...payForm, paid_date: e.target.value }); }}
              required
              fullWidth
              {...payFields.props('paid_date')}
            />
            <TextField
              size="small"
              label="Amount Paid ($)"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={payForm.amount}
              onChange={(e) => { payFields.clear('amount'); setPayForm({ ...payForm, amount: e.target.value }); }}
              fullWidth
              {...payFields.props('amount', 'Optional')}
            />
            <TextField
              size="small"
              label="Notes"
              value={payForm.notes}
              onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePay} disabled={saving}>
            {saving ? 'Saving…' : 'Mark Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit a shift */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Shift</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              label="Clock In"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={editForm.clock_in}
              onChange={(e) => { editFields.clear('clock_in'); setEditForm({ ...editForm, clock_in: e.target.value }); }}
              required
              fullWidth
              {...editFields.props('clock_in')}
            />
            <TextField
              size="small"
              label="Clock Out"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={editForm.clock_out}
              onChange={(e) => { editFields.clear('clock_out'); setEditForm({ ...editForm, clock_out: e.target.value }); }}
              fullWidth
              {...editFields.props('clock_out', editTarget?.clock_out ? undefined : 'Leave blank if they are still working')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteEntryTarget}
        title="Delete Shift"
        message={`Permanently delete the shift starting ${formatUtcDateTime(deleteEntryTarget?.clock_in)}? Its hours will no longer count toward this worker's pay. This cannot be undone.`}
        onConfirm={handleDeleteEntry}
        onCancel={() => setDeleteEntryTarget(null)}
      />

      <ConfirmDialog
        open={!!deletePaymentTarget}
        title="Delete Payment"
        message={`Permanently delete the ${formatDate(deletePaymentTarget?.paid_date)} payment to ${deletePaymentTarget?.name}? Its shifts go back to unpaid and the carryover from the payment before it is restored.`}
        onConfirm={handleDeletePayment}
        onCancel={() => setDeletePaymentTarget(null)}
      />
    </Box>
  );
};

export default AdminTimeClockPage;
